import { Helmet } from 'react-helmet-async';
import { Fragment, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Card,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TableContainer,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  getFirestore, collection, getDocs, query, where,
  addDoc, deleteDoc, updateDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import MenuItem from '@mui/material/MenuItem';
import { sortLanes, laneLabel } from '../utils/lanes';

const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

// Días de la semana para los bloqueos que se repiten (Dom=0, como getDay())
const WEEKDAYS = [
  { value: 1, short: 'L', label: 'lunes' },
  { value: 2, short: 'M', label: 'martes' },
  { value: 3, short: 'M', label: 'miércoles' },
  { value: 4, short: 'J', label: 'jueves' },
  { value: 5, short: 'V', label: 'viernes' },
  { value: 6, short: 'S', label: 'sábado' },
  { value: 0, short: 'D', label: 'domingo' },
];

const weekdaysLabel = (days = []) => {
  if (!days || days.length === 0) return '';
  return WEEKDAYS.filter((w) => days.includes(w.value)).map((w) => w.label).join(', ');
};

// Lista de fechas desde `start` hasta `end` (inclusive). Si no hay `end`, solo
// `start`. Si se pasan `weekdays` (ej: [1, 3] = lunes y miércoles), solo se
// crean esos días — así un bloqueo que se repite se pone UNA vez.
// Topes: se recorre hasta un año y se crean máximo 120 fechas, para que un
// error de dedo no llene la base de datos.
const MAX_DATES = 120;
const datesInRange = (start, end, weekdays = []) => {
  const out = [];
  const d = new Date(`${start}T12:00:00`);
  const last = new Date(`${end || start}T12:00:00`);
  let guard = 0;
  while (d <= last && out.length < MAX_DATES && guard < 400) {
    guard += 1;
    if (weekdays.length === 0 || weekdays.includes(d.getDay())) {
      out.push(d.toISOString().split('T')[0]);
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
};

// Los bloqueos creados ANTES de que existieran las series (ej: los 15 días
// seguidos de "Reparacion" que se pusieron uno por uno) se agrupan por motivo +
// horario + lanes, para poder borrarlos todos de un tiro igual que una serie.
const legacyKey = (parts) => `viejo_${parts.map((x) => String(x ?? '')).join('|')}`;

// Id para amarrar todas las fechas creadas de un tiro: así se borran juntas
const newSeriesId = () => `serie_${Date.now()}_${Math.floor(Math.random() * 10000)}`;

// Texto de la fila: "2026-09-01 → 2026-12-15 · 15 fechas (lunes)"
const seriesDateLabel = (dates, weekdays) => {
  if (dates.length <= 1) return dates[0] || '—';
  const sorted = [...dates].sort();
  const dias = weekdaysLabel(weekdays);
  return `${sorted[0]} → ${sorted[sorted.length - 1]} · ${sorted.length} fechas${dias ? ` (${dias})` : ''}`;
};

// Selector de días de la semana para los bloqueos que se repiten
function WeekdayPicker({ value, onChange, disabled }) {
  const toggle = (d) => onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d]);
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Repetir solo estos días</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        {WEEKDAYS.map((w) => (
          <Button
            key={w.value}
            size="small"
            disabled={disabled}
            variant={value.includes(w.value) ? 'contained' : 'outlined'}
            onClick={() => toggle(w.value)}
            sx={{ minWidth: 40 }}
            title={w.label}
          >
            {w.short}
          </Button>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {disabled
          ? 'Pon una fecha en "Hasta" para poder repetir (ej: todos los lunes hasta diciembre).'
          : 'Déjalos apagados para bloquear TODOS los días del rango. Marcados = solo esos días, hasta la fecha de "Hasta".'}
      </Typography>
    </Box>
  );
}

export default function LaneBlocksPage() {
  const [allLanes, setAllLanes] = useState([]);
  const [categories, setCategories] = useState([]);

  // Cancelaciones por fecha (blocked_dates): cerrar un curso un día feriado,
  // o cerrar el local completo. No son lanes, por eso van aparte.
  const [dateBlocks, setDateBlocks] = useState([]);
  const [dayOpen, setDayOpen] = useState(false);
  const [daySaving, setDaySaving] = useState(false);
  const [dayError, setDayError] = useState(null);
  const emptyDayForm = { date: '', endDate: '', weekdays: [], categoryId: '', allDay: true, startTime: '', endTime: '', reason: '' };
  const [dayForm, setDayForm] = useState(emptyDayForm);

  // Los bloqueos creados de un tiro (rango o repetición) se agrupan en una
  // sola fila por `blockSeriesId`, para verlos y borrarlos juntos.
  const loadDateBlocks = async () => {
    const db = getFirestore();
    const snap = await getDocs(collection(db, 'blocked_dates'));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    const bySeries = new Map();
    list.forEach((b) => {
      const key = b.blockSeriesId || legacyKey([b.categoryId, b.startTime, b.endTime, b.reason]);
      if (!bySeries.has(key)) bySeries.set(key, { key, items: [], weekdays: b.blockWeekdays || [] });
      bySeries.get(key).items.push(b);
    });
    setDateBlocks([...bySeries.values()]);
  };

  const handleCreateDayBlock = async () => {
    if (!dayForm.date) {
      setDayError('Escoge la fecha.');
      return;
    }
    if (!dayForm.allDay && (!dayForm.startTime || !dayForm.endTime)) {
      setDayError('Indica hora de inicio y fin, o marca "todo el día".');
      return;
    }
    if (dayForm.endDate && dayForm.endDate < dayForm.date) {
      setDayError('La fecha "hasta" no puede ser antes de la fecha inicial.');
      return;
    }
    setDaySaving(true);
    setDayError(null);
    try {
      const db = getFirestore();
      const dates = datesInRange(dayForm.date, dayForm.endDate, dayForm.weekdays);
      if (dates.length === 0) {
        setDayError('Ese rango no tiene ninguno de los días marcados.');
        setDaySaving(false);
        return;
      }
      const seriesId = newSeriesId();
      await Promise.all(dates.map((dt) => addDoc(collection(db, 'blocked_dates'), {
        date: dt,
        // null = aplica a TODAS las categorías ese día
        categoryId: dayForm.categoryId || null,
        // null = día completo
        startTime: dayForm.allDay ? null : dayForm.startTime,
        endTime: dayForm.allDay ? null : dayForm.endTime,
        laneId: null,
        reason: dayForm.reason || null,
        // Todas las fechas creadas juntas comparten serie
        blockSeriesId: seriesId,
        blockWeekdays: dayForm.weekdays,
        createdAt: serverTimestamp(),
      })));
      setMessage({
        type: 'success',
        text: dates.length > 1
          ? `${dates.length} fechas bloqueadas${dayForm.weekdays.length ? ` (${weekdaysLabel(dayForm.weekdays)})` : ''}. Se borran todas juntas desde la lista.`
          : 'Bloqueo creado. Ese horario ya no sale disponible.',
      });
      setDayOpen(false);
      setDayForm(emptyDayForm);
      loadDateBlocks();
    } catch (e) {
      console.error(e);
      setDayError('Error creando el bloqueo.');
    }
    setDaySaving(false);
  };

  const handleDeleteDayBlock = async (ids) => {
    const list = Array.isArray(ids) ? ids : [ids];
    const msg = list.length > 1
      ? `¿Eliminar las ${list.length} fechas de este bloqueo?`
      : '¿Eliminar este bloqueo?';
    if (!window.confirm(msg)) return;
    try {
      const db = getFirestore();
      await Promise.all(list.map((id) => deleteDoc(doc(db, 'blocked_dates', id))));
      loadDateBlocks();
    } catch (e) {
      console.error(e);
    }
  };

  const categoryNameById = (id) => categories.find((c) => c.id === id)?.name || 'Todas las categorías';
  const [blocks, setBlocks] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [selectedLanes, setSelectedLanes] = useState({});
  const [date, setDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [weekdays, setWeekdays] = useState([]);
  // Filas de serie abiertas (para ver las fechas una por una)
  const [expanded, setExpanded] = useState({});
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState(null);

  const fetchData = async () => {
    const db = getFirestore();
    // Firestore devuelve los lanes en orden arbitrario (Lane 6, 5, 12, 10...).
    // Se ordenan por categoría y número, igual que en el calendario.
    const [lanesSnap, catsSnap] = await Promise.all([
      getDocs(query(collection(db, 'lanes'), where('active', '==', true))),
      getDocs(collection(db, 'reservation_categories')),
    ]);
    const cats = catsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setCategories(cats);
    setAllLanes(sortLanes(lanesSnap.docs.map((d) => ({ id: d.id, ...d.data() })), cats));

    // Sin orderBy a propósito: `isBlock` + orden por fecha pide un índice
    // compuesto que no existe en el proyecto, y la página tiraba
    // "The query requires an index" y salía SIEMPRE vacía ("No hay bloqueos").
    // Se ordena aquí abajo en JavaScript.
    const blocksSnap = await getDocs(query(
      collection(db, 'reservations'),
      where('isBlock', '==', true)
    ));
    const grouped = {};
    blocksSnap.docs.forEach((d) => {
      const data = d.data();
      const key = data.blockGroupId;
      if (!grouped[key]) {
        grouped[key] = {
          blockGroupId: key,
          date: data.date,
          startTime: data.startTime,
          endTime: data.endTime,
          reason: data.blockReason,
          seriesId: data.blockSeriesId || null,
          weekdays: data.blockWeekdays || [],
          lanes: [],
        };
      }
      grouped[key].lanes.push(data.laneName);
    });
    // Segundo nivel: los días creados de un tiro salen en UNA fila
    const series = new Map();
    Object.values(grouped).forEach((g) => {
      const key = g.seriesId || legacyKey([g.startTime, g.endTime, g.reason, [...g.lanes].sort().join(',')]);
      if (!series.has(key)) series.set(key, { key, weekdays: g.weekdays, groups: [] });
      series.get(key).groups.push(g);
    });
    const list = [...series.values()];
    list.forEach((sr) => sr.groups.sort((a, b) => String(a.date).localeCompare(String(b.date))));
    list.sort((a, b) => String(b.groups[0].date).localeCompare(String(a.groups[0].date)));
    setBlocks(list);
  };

  useEffect(() => {
    fetchData();
    loadDateBlocks();
  }, []);

  const resetForm = () => {
    setSelectedLanes({});
    setDate('');
    setEndDate('');
    setWeekdays([]);
    setStartTime('');
    setEndTime('');
    setReason('');
    setFormError(null);
  };

  const handleOpen = () => { resetForm(); setOpen(true); };
  const handleClose = () => { setOpen(false); resetForm(); };

  const toggleLane = (id) => setSelectedLanes({ ...selectedLanes, [id]: !selectedLanes[id] });

  const handleCreate = async () => {
    const lanesArr = Object.entries(selectedLanes).filter(([, v]) => v).map(([k]) => k);
    if (lanesArr.length === 0 || !date || !startTime || !endTime) {
      setFormError('Selecciona lanes, fecha y horas.');
      return;
    }
    if (endDate && endDate < date) {
      setFormError('La fecha "hasta" no puede ser antes de la fecha inicial.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      // Rango de fechas y, si se marcaron, solo esos días de la semana: el
      // bloqueo que se repite se pone UNA vez (pedido del cliente).
      const dates = datesInRange(date, endDate, weekdays);
      if (dates.length === 0) {
        setFormError('Ese rango no tiene ninguno de los días marcados.');
        setSaving(false);
        return;
      }
      const seriesId = newSeriesId();
      const db = getFirestore();
      let okCount = 0;
      let lastError = null;
      // eslint-disable-next-line no-restricted-syntax
      for (const dt of dates) {
        // eslint-disable-next-line no-await-in-loop
        const r = await fetch(`${FUNCTIONS_BASE}/createLaneBlock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lanes: lanesArr,
            date: dt,
            startTime,
            endTime,
            reason,
            createdBy: 'admin',
          }),
        });
        // eslint-disable-next-line no-await-in-loop
        const data = await r.json();
        if (r.ok) {
          okCount += 1;
          // Se marcan los bloqueos con la serie para poder borrarlos juntos
          // eslint-disable-next-line no-await-in-loop
          await Promise.all((data.lanes || []).map((l) => updateDoc(
            doc(db, 'reservations', l.id),
            { blockSeriesId: seriesId, blockWeekdays: weekdays }
          ).catch(() => null)));
        } else lastError = data.error;
      }
      if (okCount > 0) {
        setMessage({
          type: lastError ? 'warning' : 'success',
          text: dates.length > 1
            ? `${okCount} de ${dates.length} día(s) bloqueado(s)${weekdays.length ? ` (${weekdaysLabel(weekdays)})` : ''}, ${lanesArr.length} lane(s) por día. Se borran todos juntos desde la lista.${lastError ? ` Último error: ${lastError}` : ''}`
            : `${lanesArr.length} lane(s) bloqueado(s).`,
        });
        handleClose();
        fetchData();
      } else {
        setFormError(lastError || 'Error creando bloqueo.');
      }
    } catch (e) {
      setFormError('Error de red.');
    }
    setSaving(false);
  };

  const handleDelete = async (blockGroupIds) => {
    const list = Array.isArray(blockGroupIds) ? blockGroupIds : [blockGroupIds];
    const msg = list.length > 1
      ? `¿Eliminar las ${list.length} fechas de este bloqueo?`
      : '¿Eliminar este bloqueo?';
    if (!window.confirm(msg)) return;
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const blockGroupId of list) {
        // eslint-disable-next-line no-await-in-loop
        await fetch(`${FUNCTIONS_BASE}/deleteLaneBlock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ blockGroupId }),
        });
      }
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const toggleExpanded = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // ---- Editar un bloqueo (toda la serie) sin borrar y volver a crear
  //      (pedido 2026-09-15). Se cambia motivo y horario; en "cancelar
  //      fecha" también la categoría. Las fechas y los lanes no se editan
  //      aquí (para eso se borra la fecha suelta o se crea otra).
  const [editTarget, setEditTarget] = useState(null); // { kind: 'date'|'lane', sr }
  const [editForm, setEditForm] = useState({ allDay: true, startTime: '', endTime: '', reason: '', categoryId: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const openEdit = (kind, sr) => {
    const first = kind === 'date' ? sr.items[0] : sr.groups[0];
    setEditForm({
      allDay: kind === 'date' ? !first.startTime : false,
      startTime: first.startTime || '',
      endTime: first.endTime || '',
      reason: first.reason || '',
      categoryId: first.categoryId || '',
    });
    setEditError(null);
    setEditTarget({ kind, sr });
  };

  const toMins = (t) => { const [h, m] = String(t || '0:0').split(':'); return parseInt(h, 10) * 60 + parseInt(m, 10); };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    const { kind, sr } = editTarget;
    if (!editForm.allDay && (!editForm.startTime || !editForm.endTime)) {
      setEditError('Indica hora de inicio y fin.');
      return;
    }
    if (!editForm.allDay && toMins(editForm.endTime) <= toMins(editForm.startTime)) {
      setEditError('La hora de fin debe ser después de la de inicio.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const db = getFirestore();
      if (kind === 'date') {
        await Promise.all(sr.items.map((i) => updateDoc(doc(db, 'blocked_dates', i.id), {
          categoryId: editForm.categoryId || null,
          startTime: editForm.allDay ? null : editForm.startTime,
          endTime: editForm.allDay ? null : editForm.endTime,
          reason: editForm.reason || null,
          updatedAt: serverTimestamp(),
        })));
        setMessage({ type: 'success', text: `Bloqueo actualizado (${sr.items.length} fecha(s)).` });
        setEditTarget(null);
        loadDateBlocks();
      } else {
        const newStart = toMins(editForm.startTime);
        const newEnd = toMins(editForm.endTime);
        const conflicts = [];
        const updates = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const g of sr.groups) {
          // eslint-disable-next-line no-await-in-loop
          const blockSnap = await getDocs(query(collection(db, 'reservations'), where('blockGroupId', '==', g.blockGroupId)));
          const laneIds = blockSnap.docs.map((d) => d.data().laneId).filter(Boolean);
          // Si el horario cambia, ninguna reservación real de esos lanes
          // puede solaparse con el nuevo horario.
          if (g.startTime !== editForm.startTime || g.endTime !== editForm.endTime) {
            // eslint-disable-next-line no-await-in-loop
            const daySnap = await getDocs(query(
              collection(db, 'reservations'),
              where('date', '==', g.date),
              where('status', 'in', ['confirmed', 'pending_payment']),
            ));
            daySnap.docs.forEach((d) => {
              const r = d.data();
              if (r.isBlock || !laneIds.includes(r.laneId)) return;
              const rs = toMins(r.startTime);
              const re = r.endTime ? toMins(r.endTime) : rs + (Number(r.durationMinutes) || 60);
              if (rs < newEnd && re > newStart) conflicts.push(`${g.date} ${r.laneName || ''}: ${r.userName} (${r.startTime}–${r.endTime})`);
            });
          }
          blockSnap.docs.forEach((d) => updates.push(d.ref));
        }
        if (conflicts.length > 0) {
          setEditError(`No se puede cambiar el horario: hay reservaciones en esos lanes. ${conflicts.slice(0, 4).join(' · ')}${conflicts.length > 4 ? ` (+${conflicts.length - 4})` : ''}`);
          setEditSaving(false);
          return;
        }
        const label = editForm.reason || 'Bloqueo (Evento/Agencia)';
        await Promise.all(updates.map((ref) => updateDoc(ref, {
          startTime: editForm.startTime,
          endTime: editForm.endTime,
          durationMinutes: newEnd - newStart,
          blockReason: editForm.reason || null,
          categoryName: label,
          userName: label,
          updatedAt: serverTimestamp(),
        })));
        setMessage({ type: 'success', text: `Bloqueo actualizado (${updates.length} lane-día(s)).` });
        setEditTarget(null);
        fetchData();
      }
    } catch (e) {
      console.error(e);
      setEditError('Error guardando los cambios.');
    }
    setEditSaving(false);
  };

  return (
    <>
      <Helmet><title>Bloqueos | BBTC</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          <Typography variant="h4">Bloqueos</Typography>
        </Stack>

        {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

        {/* Cancelar un curso/día (feriados, cierres) */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">Cancelar curso o día</Typography>
            <Typography variant="body2" color="text.secondary">
              Cierra una categoría (o el local completo) en una fecha. Ejemplo: es feriado
              y no se da el curso del martes.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setDayForm(emptyDayForm); setDayError(null); setDayOpen(true); }}>
            Cancelar fecha
          </Button>
        </Stack>

        <Card sx={{ mb: 5 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell>Horario</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {dateBlocks.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>No hay fechas canceladas.</TableCell></TableRow>
                ) : dateBlocks.map((sr) => {
                  const first = sr.items[0];
                  const isSeries = sr.items.length > 1;
                  const isOpen = !!expanded[sr.key];
                  return (
                    <Fragment key={sr.key}>
                      <TableRow hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {isSeries && (
                              <IconButton size="small" onClick={() => toggleExpanded(sr.key)}>
                                {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                              </IconButton>
                            )}
                            <span>{seriesDateLabel(sr.items.map((i) => i.date), sr.weekdays)}</span>
                          </Stack>
                        </TableCell>
                        <TableCell>{categoryNameById(first.categoryId)}</TableCell>
                        <TableCell>{first.startTime ? `${first.startTime} - ${first.endTime}` : 'Todo el día'}</TableCell>
                        <TableCell>{first.reason || '—'}</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => openEdit('date', sr)} title="Editar motivo / horario">
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteDayBlock(sr.items.map((i) => i.id))}
                            title={isSeries ? 'Eliminar todas las fechas' : 'Eliminar'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {isSeries && isOpen && sr.items.map((i) => (
                        <TableRow key={i.id}>
                          <TableCell sx={{ pl: 7, color: 'text.secondary' }}>{i.date}</TableCell>
                          <TableCell colSpan={3} />
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleDeleteDayBlock(i.id)} title="Eliminar solo esta fecha">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Bloqueos de lane (eventos/agencias) */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">Bloqueos de lane</Typography>
            <Typography variant="body2" color="text.secondary">
              Reserva lanes específicos para un evento o agencia.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
            Añadir bloqueo
          </Button>
        </Stack>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Horario</TableCell>
                  <TableCell>Lanes</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {blocks.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>No hay bloqueos.</TableCell></TableRow>
                ) : blocks.map((sr) => {
                  const first = sr.groups[0];
                  const isSeries = sr.groups.length > 1;
                  const isOpen = !!expanded[sr.key];
                  return (
                    <Fragment key={sr.key}>
                      <TableRow hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {isSeries && (
                              <IconButton size="small" onClick={() => toggleExpanded(sr.key)}>
                                {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                              </IconButton>
                            )}
                            <span>{seriesDateLabel(sr.groups.map((g) => g.date), sr.weekdays)}</span>
                          </Stack>
                        </TableCell>
                        <TableCell>{first.startTime} - {first.endTime}</TableCell>
                        <TableCell>{first.lanes.join(', ')}</TableCell>
                        <TableCell>{first.reason || '—'}</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => openEdit('lane', sr)} title="Editar motivo / horario">
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(sr.groups.map((g) => g.blockGroupId))}
                            title={isSeries ? 'Eliminar todas las fechas' : 'Eliminar'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {isSeries && isOpen && sr.groups.map((g) => (
                        <TableRow key={g.blockGroupId}>
                          <TableCell sx={{ pl: 7, color: 'text.secondary' }}>{g.date}</TableCell>
                          <TableCell>{g.startTime} - {g.endTime}</TableCell>
                          <TableCell>{g.lanes.join(', ')}</TableCell>
                          <TableCell />
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleDelete(g.blockGroupId)} title="Eliminar solo esta fecha">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Editar bloqueo (serie completa) */}
      <Dialog open={!!editTarget} onClose={() => !editSaving && setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editTarget?.kind === 'date' ? 'Editar fecha cancelada' : 'Editar bloqueo de lane'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {editError && <Alert severity="error">{editError}</Alert>}
            {editTarget && (
              <Alert severity="info">
                Se aplica a {editTarget.kind === 'date' ? editTarget.sr.items.length : editTarget.sr.groups.length} fecha(s):{' '}
                {seriesDateLabel(
                  editTarget.kind === 'date' ? editTarget.sr.items.map((i) => i.date) : editTarget.sr.groups.map((g) => g.date),
                  editTarget.sr.weekdays,
                )}
                {editTarget.kind === 'lane' ? ` · Lanes: ${editTarget.sr.groups[0].lanes.join(', ')}` : ''}
              </Alert>
            )}
            {editTarget?.kind === 'date' && (
              <>
                <TextField
                  select
                  label="Categoría"
                  value={editForm.categoryId}
                  onChange={(e) => setEditForm({ ...editForm, categoryId: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="">Todas (cierra el local ese día)</MenuItem>
                  {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
                <FormControlLabel
                  control={<Checkbox checked={editForm.allDay} onChange={(e) => setEditForm({ ...editForm, allDay: e.target.checked })} />}
                  label="Todo el día"
                />
              </>
            )}
            {!editForm.allDay && (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Inicio"
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  value={editForm.startTime}
                  onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Fin"
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  value={editForm.endTime}
                  onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                  fullWidth
                />
              </Stack>
            )}
            <TextField
              label={editTarget?.kind === 'lane' ? 'Título / motivo (sale en el calendario)' : 'Motivo'}
              value={editForm.reason}
              onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
              fullWidth
              placeholder="Ej. Cualificación Policía"
            />
            <Typography variant="caption" color="text.secondary">
              Para cambiar fechas o lanes: elimina la fecha suelta desde la lista o crea un bloqueo nuevo.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={editSaving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editSaving}>
            {editSaving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dayOpen} onClose={() => !daySaving && setDayOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar curso o día</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {dayError && <Alert severity="error">{dayError}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Fecha"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={dayForm.date}
                onChange={(e) => setDayForm({ ...dayForm, date: e.target.value })}
                fullWidth
              />
              <TextField
                label="Hasta (opcional)"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={dayForm.endDate}
                onChange={(e) => setDayForm({ ...dayForm, endDate: e.target.value })}
                fullWidth
                helperText="Para cerrar varios días seguidos"
              />
            </Stack>
            <WeekdayPicker
              value={dayForm.weekdays}
              onChange={(v) => setDayForm({ ...dayForm, weekdays: v })}
              disabled={!dayForm.endDate}
            />
            <TextField
              select
              label="Categoría"
              value={dayForm.categoryId}
              onChange={(e) => setDayForm({ ...dayForm, categoryId: e.target.value })}
              helperText="Deja 'Todas' para cerrar el local completo ese día."
              fullWidth
            >
              <MenuItem value="">Todas las categorías</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dayForm.allDay}
                  onChange={(e) => setDayForm({ ...dayForm, allDay: e.target.checked })}
                />
              }
              label="Todo el día"
            />
            {!dayForm.allDay && (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Hora inicio (HH:mm)"
                  placeholder="18:00"
                  value={dayForm.startTime}
                  onChange={(e) => setDayForm({ ...dayForm, startTime: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Hora fin (HH:mm)"
                  placeholder="20:00"
                  value={dayForm.endTime}
                  onChange={(e) => setDayForm({ ...dayForm, endTime: e.target.value })}
                  fullWidth
                />
              </Stack>
            )}
            <TextField
              label="Motivo / Descripción"
              value={dayForm.reason}
              onChange={(e) => setDayForm({ ...dayForm, reason: e.target.value })}
              fullWidth
              placeholder="Ej: Feriado — no se da el curso"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayOpen(false)} disabled={daySaving}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateDayBlock} disabled={daySaving}>
            {daySaving ? 'Guardando...' : 'Crear bloqueo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Crear bloqueo de lane</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Fecha"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
              />
              <TextField
                label="Hasta (opcional)"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                helperText="Para bloquear de tal día a tal día"
              />
            </Stack>
            <WeekdayPicker value={weekdays} onChange={setWeekdays} disabled={!endDate} />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Hora inicio (HH:mm)"
                placeholder="14:00"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                fullWidth
              />
              <TextField
                label="Hora fin (HH:mm)"
                placeholder="16:00"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              label="Motivo / Descripción"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              placeholder="Ej: Evento Agencia X"
            />
            <Divider />
            <Typography variant="subtitle2">Lanes a bloquear</Typography>
            <FormGroup>
              {allLanes.map((l) => (
                <FormControlLabel
                  key={l.id}
                  control={<Checkbox checked={selectedLanes[l.id] || false} onChange={() => toggleLane(l.id)} />}
                  label={laneLabel(l, categories)}
                />
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creando...' : 'Crear bloqueo'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
